import {
  api,
  User,
  UserFull,
  PostApiV1UsersFollowByUsernameApiArg,
  GetApiV1UsersApiArg,
  fetchInterceptor,
} from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { getErrorMsg } from '@wellesley/error-kit'
import { toast } from '@wellesley/system-toast'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import {
  setUserPending,
  setUserData,
  setUserSuccess,
  setUserIsError,
  setUserError,
  updateUser,
} from '@wellesley/host-remote/api/actions'
import { type RootState, getStore } from '@wellesley/store'
import { userSlice } from './slices/userSlice'
import { isEqual } from 'lodash/fp'
import optimisticUpdateRelations from './helpers/relationship/optimisticUpdateRelations'
import { getUsernameConflictError } from '@wellesley/forms'
import { isHiddenListFetchError } from './helpers/isHiddenListError'

export type FollowMutationCommonProps = PostApiV1UsersFollowByUsernameApiArg & {
  user: User | UserFull
}

export const userApi = api
  .enhanceEndpoints({
    endpoints: {
      deleteApiV1Users: {
        invalidatesTags: ['Secure', 'Account'],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const res = await handleBasicResponse(queryFulfilled)
            if (res) {
              await dispatch(
                api.endpoints.getApiV1AccountsProfile.initiate({
                  htmlContent: false,
                }),
              ).unwrap()
            }
          } catch (e) {
            toast.error(`${getErrorMsg(e as FetchBaseQueryError)}`)
          }
        },
      },
      getApiV1AccountsProfile: {
        providesTags: ['Secure', 'Account'],
        async onCacheEntryAdded(
          _,
          { dispatch, cacheDataLoaded, cacheEntryRemoved },
        ) {
          await cacheDataLoaded
          const off = fetchInterceptor.on<UserFull>({
            url: '/api/v1/users/:userId',
            method: 'PUT',
            callback: ({ response }) => {
              const updatedUser = response.body
              if (updatedUser) dispatch(updateUser(updatedUser))
            },
          })
          await cacheEntryRemoved
          off()
        },
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          dispatch(setUserPending(true))
          try {
            const res = await handleBasicResponse(queryFulfilled)
            if (res) {
              dispatch(setUserData(res.data))
            }
            dispatch(setUserSuccess(true))
          } catch (e) {
            dispatch(setUserSuccess(false))
            dispatch(setUserIsError(true))
            dispatch(setUserError(e))
          } finally {
            dispatch(setUserPending(false))
          }
        },
      },
      putApiV1UsersByUserId: {
        invalidatesTags: ['Secure'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
            toast.success('User updated successfully')
          } catch (e: unknown) {
            const error = (e as { error: FetchBaseQueryError }).error

            const { isError: isConflictError } = getUsernameConflictError(error)

            const errorMsg = getErrorMsg(error)
            !isConflictError && errorMsg && toast.error(errorMsg)
          }
        },
      },
      getApiV1Users: {
        providesTags: (_result, _error, args) => [
          'Secure',
          { type: 'User', id: args.htmlContent ? 'HTML' : 'NO_HTML' },
        ],
        extraOptions: {
          maxRetries: 0,
          initialDataKey: 'user',
          validateInitialDataParams: ({
            requestArgs,
          }: {
            requestArgs: { params: GetApiV1UsersApiArg }
          }) => {
            return Boolean(
              requestArgs.params.full && requestArgs.params.htmlContent,
            )
          },
        },
        async onCacheEntryAdded(
          _,
          { dispatch, cacheDataLoaded, cacheEntryRemoved },
        ) {
          await cacheDataLoaded
          const store = getStore()
          if (!store) return

          let prevUsers = userSlice.selectors.selectAllUsers(
            store.getState() as RootState,
          )
          const unsubscribe = store.subscribe(() => {
            const newUsers = userSlice.selectors.selectAllUsers(
              store.getState() as RootState,
            )
            if (prevUsers !== newUsers && !isEqual(prevUsers, newUsers)) {
              prevUsers = newUsers
              dispatch(api.util.invalidateTags(['User']))
            } else if (prevUsers !== newUsers) {
              prevUsers = newUsers
            }
          })

          await cacheEntryRemoved
          unsubscribe()
        },
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await handleBasicResponse(queryFulfilled)
          } catch (e) {
            toast.error(`${getErrorMsg(e as FetchBaseQueryError)}`)
          }
        },
      },
      postApiV1Users: {
        invalidatesTags: ['Account'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
            toast.success('User created successfully')
          } catch (e: unknown) {
            const error = (e as { error: FetchBaseQueryError }).error

            const { isError: isConflictError } = getUsernameConflictError(error)

            !isConflictError && toast.error(`${getErrorMsg(error)}`)
          }
        },
      },
      getApiV1UsersFollowers: {
        providesTags: ['Follow'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
          } catch (e) {
            if (!isHiddenListFetchError(e)) {
              const error = e as { error: FetchBaseQueryError }
              toast.error(`${getErrorMsg(error.error)}`)
            }
          }
        },
      },
      getApiV1UsersRemoteByUserNameFullFollowers: {
        providesTags: ['Follow'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
          } catch (e) {
            if (!isHiddenListFetchError(e)) {
              const error = e as { error: FetchBaseQueryError }
              toast.error(`${getErrorMsg(error.error)}`)
            }
          }
        },
      },
      getApiV1UsersByUserId: {
        providesTags: (_result, _error, args) => [
          'Secure',
          { type: 'User', id: args.htmlContent ? 'HTML' : 'NO_HTML' },
        ],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await handleBasicResponse(queryFulfilled)
          } catch (e) {
            toast.error(`${getErrorMsg(e as FetchBaseQueryError)}`)
          }
        },
      },
      getApiV1UsersByUserIdFollowers: {
        providesTags: ['Follow'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
          } catch (e) {
            if (!isHiddenListFetchError(e)) {
              const error = e as { error: FetchBaseQueryError }
              toast.error(`${getErrorMsg(error.error)}`)
            }
          }
        },
      },
      getApiV1UsersFollowees: {
        providesTags: ['Follow'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
          } catch (e) {
            if (!isHiddenListFetchError(e)) {
              const error = e as { error: FetchBaseQueryError }
              toast.error(`${getErrorMsg(error.error)}`)
            }
          }
        },
      },
      getApiV1UsersByUserIdFollowing: {
        providesTags: ['Follow'],
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled
          } catch (e) {
            if (!isHiddenListFetchError(e)) {
              const error = e as { error: FetchBaseQueryError }
              toast.error(`${getErrorMsg(error.error)}`)
            }
          }
        },
      },
      postApiV1UsersFollowByUsernameAccept: {
        invalidatesTags: ['Follow'],
        async onQueryStarted(
          props: FollowMutationCommonProps,
          { queryFulfilled, dispatch, getState },
        ) {
          const patchResult = optimisticUpdateRelations({
            dispatch,
            userId: props.user.id,
            state: getState() as RootState,
            relationsProps: { followed: 'ACCEPTED' },
          })

          await handleBasicResponse(
            queryFulfilled,
            `Accepted follow request from ${props.username}`,
            () => patchResult?.undo(),
          )
        },
      },

      postApiV1UsersFollowByUsernameReject: {
        invalidatesTags: ['Follow'],
        async onQueryStarted(
          props: FollowMutationCommonProps,
          { queryFulfilled, dispatch, getState },
        ) {
          const patchResult = optimisticUpdateRelations({
            dispatch,
            userId: props.user.id,
            state: getState() as RootState,
            relationsProps: { followed: 'REJECTED' },
          })

          await handleBasicResponse(
            queryFulfilled,
            `Rejected follow request from ${props.username}`,
            () => patchResult?.undo(),
          )
        },
      },
      postApiV1UsersFollowByUsername: {
        invalidatesTags: ['Follow', 'Secure'],
        async onQueryStarted(
          props: FollowMutationCommonProps,
          { queryFulfilled, dispatch },
        ) {
          const res = await handleBasicResponse(
            queryFulfilled,
            `Follow request sent to ${props.username}`,
          )

          res &&
            dispatch(
              api.util.invalidateTags([
                { type: 'Relationship', id: props.user.id },
              ]),
            )
        },
      },
      postApiV1UsersFollowByUsernameUndo: {
        invalidatesTags: ['Follow', 'Secure'],
        async onQueryStarted(
          props: FollowMutationCommonProps,
          { queryFulfilled, dispatch, getState },
        ) {
          const patchResult = optimisticUpdateRelations({
            dispatch,
            userId: props.user.id,
            state: getState() as RootState,
            relationsProps: { following: null },
          })

          await handleBasicResponse(
            queryFulfilled,
            `You unfollowed ${props.username}`,
            () => patchResult?.undo(),
          )
        },
      },
      getApiV1Block: {
        providesTags: ['Follow'],
      },
      postApiV1BlockByUserId: {
        invalidatesTags: ['Follow'],
        async onQueryStarted(args, { queryFulfilled, getState, dispatch }) {
          const patchResult = optimisticUpdateRelations({
            userId: args.userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: { blocked: true },
          })

          await handleBasicResponse(queryFulfilled, undefined, () =>
            patchResult?.undo(),
          )
        },
      },
      deleteApiV1BlockByUserId: {
        invalidatesTags: ['Follow'],
        async onQueryStarted(args, { queryFulfilled, getState, dispatch }) {
          const patchResult = optimisticUpdateRelations({
            userId: args.userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: {
              blocked: false,
              muted: {
                state: false,
              },
            },
          })
          await handleBasicResponse(queryFulfilled, undefined, () =>
            patchResult?.undo(),
          )
        },
      },

      postApiV1MuteByUserId: {
        async onQueryStarted(
          { userId },
          { queryFulfilled, getState, dispatch },
        ) {
          const patchResult = optimisticUpdateRelations({
            userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: {
              muted: {
                state: true,
              },
            },
          })

          await handleBasicResponse(queryFulfilled, undefined, () =>
            patchResult?.undo(),
          )
        },
      },
      deleteApiV1MuteByUserId: {
        async onQueryStarted(
          { userId },
          { queryFulfilled, getState, dispatch },
        ) {
          const patchResult = optimisticUpdateRelations({
            userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: {
              muted: {
                state: false,
              },
            },
          })
          await handleBasicResponse(
            queryFulfilled,
            undefined,

            () => patchResult?.undo(),
          )
        },
      },
      getApiV1Mute: {
        async onQueryStarted(_, { queryFulfilled }) {
          await handleBasicResponse(queryFulfilled)
        },
      },
      getApiV1UsersRelationships: {
        providesTags: (_result, _error, { targets: userIds }) =>
          userIds.map((id) => ({ type: 'Relationship', id })),
        async onQueryStarted(_, { queryFulfilled }) {
          await handleBasicResponse(queryFulfilled)
        },
      },
      postApiV1AdminUserChangeState: {
        invalidatesTags: ['Secure'],
        async onQueryStarted(args, { queryFulfilled }) {
          await handleBasicResponse(
            queryFulfilled,
            `User ${args.changeUserState.userId} state changed to ${args.changeUserState.newState}`,
          )
        },
      },
      putApiV1Notes: {
        async onQueryStarted(args, { queryFulfilled, getState, dispatch }) {
          const { userId, text } = args.noteUpdate

          const patchResult = optimisticUpdateRelations({
            userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: { note: text },
          })

          await handleBasicResponse(
            queryFulfilled,
            `Note has been updated`,
            () => patchResult?.undo(),
          )
        },
      },
      deleteApiV1NotesByUserId: {
        async onQueryStarted(args, { queryFulfilled, getState, dispatch }) {
          const patchResult = optimisticUpdateRelations({
            userId: args.userId,
            dispatch,
            state: getState() as RootState,
            relationsProps: { note: '' },
          })

          await handleBasicResponse(
            queryFulfilled,
            `Note has been removed`,
            () => patchResult?.undo(),
          )
        },
      },
      postApiV1UsersFollowByUsernamePend: {
        async onQueryStarted(args, { queryFulfilled }) {
          await handleBasicResponse(
            queryFulfilled,
            `${args.username} has been removed from followers`,
          )
        },
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getInteractionUrl: build.query<Response, string>({
        query: (url: string) => ({
          url,
          headers: { Accept: 'application/activity+json' },
        }),
        transformResponse: (response: string) => {
          return typeof response === 'string'
            ? (JSON.parse(response) as Response)
            : (response as Response)
        },
      }),
    }),
  })

export const {
  useGetInteractionUrlQuery,
  usePostApiV1UsersMutation,
  useDeleteApiV1AccountsDeleteMutation,
  useGetApiV1AccountsProfileQuery,
  usePutApiV1UsersByUserIdMutation,
  useGetApiV1UsersQuery,
  useDeleteApiV1UsersMutation,
  usePostApiV1MuteByUserIdMutation,
  useDeleteApiV1MuteByUserIdMutation,
  useGetApiV1MuteQuery,
  usePostApiV1AdminUserChangeStateMutation,
  useGetApiV1UsersRelationshipsQuery,
  usePutApiV1NotesMutation,
  useDeleteApiV1NotesByUserIdMutation,
  useLazyGetApiV1UsersRelationshipsQuery,
  useLazyGetApiV1GeoQuery,
  useLazyGetApiV1GroupsOwnedQuery,
  useGetApiV1UsersFollowersQuery,
  useGetApiV1UsersByUserIdQuery,
  useLazyGetApiV1UsersRenameHistoryQuery,
  useGetApiV1UsersByUserIdFollowersQuery,
  useLazyGetApiV1UsersByUserIdFollowersQuery,
  useGetApiV1UsersByUserIdFollowingQuery,
  useGetApiV1UsersFolloweesQuery,
  usePostApiV1UsersFollowByUsernameMutation,
  usePostApiV1UsersFollowByUsernameUndoMutation,
  usePostApiV1UsersFollowByUsernameRejectMutation,
  usePostApiV1UsersFollowByUsernameAcceptMutation,
  useGetApiV1UsersRemoteByUserNameFullFollowersQuery,
  useLazyGetApiV1UsersRemoteByUserNameFullFollowersQuery,
} = userApi
